import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { w3Lines } from '@/mocks/w3';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

export default function W3Page() {
  const [activeTab, setActiveTab] = useState(w3Lines[0].id);

  useSEO({
    title: 'W3 Indústria Caruaru PE | Mobiliários de Aço – Home Office Premium',
    description: 'W3 Indústria em Caruaru PE. Armários, estantes, arquivos e lockers de aço com pintura eletrostática. Normas NR18 e NR24. Revendedor autorizado.',
    keywords: 'W3 Indústria Caruaru, armários aço Pernambuco, lockers corporativos, estantes aço, móveis aço escritório',
    path: '/w3-industria',
    ogTitle: 'W3 Indústria Caruaru PE | Home Office Premium',
    ogDescription: 'Revendedor autorizado W3 Indústria em Caruaru PE. Armários, estantes e lockers de aço.',
  });

  useSchemaLD({
    id: 'schema-w3',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'W3 Indústria Caruaru PE – Home Office Premium',
      description: 'Revendedor autorizado W3 Indústria em Caruaru PE. Armários, estantes, arquivos e lockers de aço com pintura eletrostática. Normas NR18 e NR24.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/w3-industria`,
      about: {
        '@type': 'Brand',
        name: 'W3 Indústria',
        description: 'Líder nacional em mobiliário corporativo de aço. Pintura eletrostática, tratamento antiferrugem, normas NR18 e NR24.',
      },
    },
  });

  useSchemaLD({
    id: 'schema-w3-breadcrumb',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com' },
        { '@type': 'ListItem', position: 2, name: 'Marcas', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marcas` },
        { '@type': 'ListItem', position: 3, name: 'W3 Indústria', item: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/w3-industria` },
      ],
    },
  });

  useSchemaLD({
    id: 'schema-w3-faq',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Onde comprar armários de aço W3 Indústria em Caruaru e Pernambuco?',
          acceptedAnswer: { '@type': 'Answer', text: 'A Home Office Premium é revendedora autorizada W3 Indústria em Caruaru PE. Temos armários, estantes, arquivos e lockers de aço com entrega e montagem em todo Pernambuco, incluindo Recife, Petrolina e Garanhuns.' },
        },
        {
          '@type': 'Question',
          name: 'Os móveis de aço W3 atendem às normas NR18 e NR24?',
          acceptedAnswer: { '@type': 'Answer', text: 'Sim. Todos os móveis de aço W3 Indústria são fabricados em conformidade com as normas NR18 e NR24, com pintura eletrostática a pó e tratamento antiferrugem, garantindo segurança e durabilidade.' },
        },
        {
          '@type': 'Question',
          name: 'Quais produtos de aço a W3 Indústria fabrica?',
          acceptedAnswer: { '@type': 'Answer', text: 'A W3 Indústria fabrica armários de aço, estantes metálicas, arquivos deslizantes, lockers corporativos e outros móveis de aço para escritórios, indústrias e ambientes corporativos.' },
        },
        {
          '@type': 'Question',
          name: 'Qual a vantagem dos móveis de aço em relação aos de madeira?',
          acceptedAnswer: { '@type': 'Answer', text: 'Móveis de aço são mais duravéis, resistentes a umidade e pragas, mais fáceis de limpar e geralmente mais seguros. São ideais para ambientes industriais, áreas de estoque, vestiários e locais que exigem maior robustez.' },
        },
      ],
    },
  });

  const activeLine = w3Lines.find(l => l.id === activeTab);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative pt-20 h-[520px] md:h-[600px] overflow-hidden bg-gray-900">
        <img
          src="/media/ia/0221.webp"
          alt="W3 Indústria"
          className="w-full h-full object-cover object-center opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-[#374151] text-white text-xs font-semibold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
                <i className="ri-star-fill text-xs"></i>
                Marca Parceira Oficial
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
                W3<br />
                <span className="text-gray-400">Indústria</span>
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8">
                Líder em mobiliário corporativo de aço. Armários, estantes, arquivos e lockers com pintura eletrostática e tratamento antiferrugem. Normas NR18 e NR24.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://www.w3industria.com.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Ver Catálogo Completo
                </a>
                <a
                  href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line"></i>
                  Solicitar Orçamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sobre */}
      <section className="py-20 bg-[#f2f2f0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">Quem é a W3 Indústria</p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Líder nacional em mobiliário corporativo de aço
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                A W3 Indústria é referência no Brasil em mobiliário de aço para ambientes corporativos, industriais e comerciais. Seus produtos são fabricados com aço de alta qualidade, pintura eletrostática a pó e tratamento antiferrugem.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                Somos revendedores autorizados da W3 Indústria em Caruaru e região, com toda a linha disponível para pronta entrega ou sob encomenda.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Normas NR18 e NR24' },
                  { icon: 'ri-truck-line', label: 'Entrega em todo Brasil' },
                  { icon: 'ri-drop-line', label: 'Tratamento Antiferrugem' },
                  { icon: 'ri-award-line', label: 'Revendedor Autorizado' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-300 rounded-lg flex-shrink-0">
                      <i className={`${item.icon} text-gray-700 text-sm`}></i>
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: 'NR18', label: 'Norma Atendida' },
                { value: 'NR24', label: 'Norma Atendida' },
                { value: 'Epóxi', label: 'Pintura Eletrostática' },
                { value: 'BR', label: 'Fabricação Nacional' },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-2xl p-6 text-center border border-gray-100">
                  <p className="text-2xl font-bold text-gray-800 mb-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Linhas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">Catálogo</p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Produtos W3 Indústria</h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto">
              Selecione um produto para ver detalhes. Clique em "Ver no site oficial" para explorar todos os modelos.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {w3Lines.map(line => (
              <button
                key={line.id}
                onClick={() => setActiveTab(line.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === line.id
                    ? 'bg-[#374151] text-white'
                    : 'bg-[#f2f2f0] text-gray-600 hover:bg-gray-200'
                }`}
              >
                {line.name}
              </button>
            ))}
          </div>

          {activeLine && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className={`grid gap-4 ${activeLine.photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                {activeLine.photos.map((photo, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl overflow-hidden bg-[#f8f8f6] flex items-center justify-center p-4 ${
                      activeLine.photos.length === 1 ? 'h-[480px]' : 'h-64'
                    }`}
                  >
                    <img
                      src={photo}
                      alt={`${activeLine.name} ${idx + 1}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ))}
              </div>
              <div>
                <p className="text-gray-500 text-xs font-semibold uppercase tracking-widest mb-3">{activeLine.category}</p>
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">{activeLine.name}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-8">{activeLine.description}</p>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={activeLine.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>
                    Ver no site W3 Indústria
                  </a>
                  <a
                    href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#f2f2f0] text-gray-700 text-sm font-medium px-6 py-3 rounded-full hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-whatsapp-line"></i>
                    Pedir orçamento
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-4">Revendedor Autorizado</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Quer equipar seu escritório com<br className="hidden md:block" /> mobiliário de aço W3?
          </h2>
          <p className="text-gray-400 text-sm mb-10 max-w-lg mx-auto">
            Fale com nossa equipe e receba um orçamento personalizado. Atendemos empresas e escritórios em Caruaru e região.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#374151] text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-[#2d3748] transition-colors whitespace-nowrap cursor-pointer">
              <i className="ri-whatsapp-line"></i>
              Falar no WhatsApp
            </a>
            <a href="https://www.w3industria.com.br" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-external-link-line"></i>
              Ver Catálogo Oficial
            </a>
            <Link to="/contato"
              className="inline-flex items-center gap-2 bg-white/10 text-white text-sm font-medium px-8 py-4 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer border border-white/20">
              <i className="ri-mail-line"></i>
              Enviar Mensagem
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
